import {StandardSchemaV1} from '@standard-schema/spec';
import {isReportableServerError} from './ErroneousServerResult';
import {HttpClient} from './HttpClient';

type CustomParser<T> = {
    parse: (response: HttpClient.SyncOutput) => T;
};

type ExpectersDict = {
    [key in number]: StandardSchemaV1<any, any> | CustomParser<any> | null;
};

type Variant<TStatus, TJson> = {status: TStatus; json: TJson};
type AbortVariant = {status: 'Abort'; json?: undefined; reason: Error};
type CrashVariant = {status: 'Crash'; json?: undefined; reason: unknown};

function createAbortResult(reason: Error): AbortVariant {
    return {status: 'Abort', reason};
}
function createCrashResult(reason: unknown): CrashVariant {
    return {status: 'Crash', reason};
}

type InferredVariant<S extends ExpectersDict> = {
    [K in keyof S]: S[K] extends StandardSchemaV1<any, infer T>
        ? Variant<K, T>
        : S[K] extends CustomParser<infer T>
          ? Variant<K, Awaited<T>>
          : S[K] extends null
            ? Variant<K, null>
            : never;
}[keyof S];

/**
 * @deprecated
 * @see {@link expectEither}
 */
export function legacy_expectOneOf<S extends ExpectersDict>(expectersDict: S) {
    const [handleExpectation] = expectEither(expectersDict);
    return handleExpectation;
}

export function expectEither<S extends ExpectersDict>(expectersDict: S) {
    function noContentReturn(response: HttpClient.SyncOutput) {
        return {status: response.status, json: null};
    }

    function customParser(expecter: CustomParser<any>) {
        return async (response: HttpClient.SyncOutput) => ({
            status: response.status,
            json: await expecter.parse(response),
        });
    }

    async function defaultRequestHandler(response: HttpClient.SyncOutput): Promise<InferredVariant<S>> {
        let parser: (response: HttpClient.SyncOutput) => any;
        const expecter = expectersDict[response.status];
        if (expecter === undefined) {
            if (response.status >= 500) {
                throw ServerError.from(response, new Error());
            }
            if (response.status === 400) {
                const clarification = await BadRequestError.clarify(response);
                throw BadRequestError.from(response, new Error(clarification));
            }
            throw UnexpectedResponseError.from(response, new Error(`Expected ${Object.keys(expectersDict)}`));
        }
        if (expecter === null) {
            parser = noContentReturn;
        } else if ('~standard' in expecter) {
            parser = parseStandardSchema(expecter);
        } else {
            parser = customParser(expecter);
        }
        return await parser(response);
    }

    return [defaultRequestHandler, defaultCrashHandler] as const;
}

export async function parseResponseAs<S>(schema: StandardSchemaV1<any, S>, response: HttpClient.SyncOutput) {
    const {json} = await parseStandardSchema(schema)(response);
    return json;
}

function parseStandardSchema<S>(schema: StandardSchemaV1<any, S>) {
    return async (response: HttpClient.SyncOutput): Promise<Variant<number, S>> => {
        const data = await prepareData(response);
        const result = schema['~standard'].validate(data);
        if (result instanceof Promise) {
            return result.then(result => unwrapStandardResult(result, response));
        } else {
            return unwrapStandardResult(result, response);
        }
    };
}

async function prepareData(response: HttpClient.SyncOutput) {
    const contentType = response.headers.get('content-type');
    if (!contentType || contentType.indexOf('json') === -1) {
        throw UnexpectedResponseError.from(response, new Error(`Expected application/json, got ${contentType}`));
    }
    return await response.json(); // if content-type is application/json, throwing on syntax error is desired behavior
}

function unwrapStandardResult<T>(result: StandardSchemaV1.Result<T>, response: HttpClient.SyncOutput) {
    if (result.issues) {
        const messages = result.issues.map(i => {
            if ('at' in i && i.at instanceof Array) {
                return `${i.at.join('')} ${i.message}`;
            }
            return i.message;
        });
        throw ResponseValidationError.from(response, new Error(messages.join('\n')));
    }
    return {status: response.status, json: result.value};
}

export function expectEitherAndCrash<S extends ExpectersDict>(expectersDict: S) {
    const [defaultResponseHandler] = expectEither(expectersDict);
    async function safeResponseHandler(response: HttpClient.SyncOutput) {
        return defaultResponseHandler(response).catch(safeCrashHandler);
    }
    return [safeResponseHandler, safeCrashHandler] as const;
}

function defaultCrashHandler(error: unknown): AbortVariant {
    if (isAbortError(error)) {
        return createAbortResult(error);
    }
    throw error;
}

function safeCrashHandler(error: unknown): AbortVariant | CrashVariant {
    if (isAbortError(error)) {
        return createAbortResult(error);
    }
    return createCrashResult(error);
}

export class ResponseValidationError extends Error {
    readonly name = 'ResponseValidationError';
    static from(output: HttpClient.SyncOutput, base: Error) {
        const {method, path} = representRequest(output.request);
        const self = new ResponseValidationError(
            `Validation failed "${method} ${path}" -> ${output.status}.\n${base.message}`
        );
        self.stack = base.stack;
        if (base.cause) self.cause = base.cause;
        return self;
    }
}

export class BadRequestError extends Error {
    readonly name = 'BadRequestError';
    static from(output: HttpClient.SyncOutput, base: Error) {
        const {method, path} = representRequest(output.request);
        const self = new BadRequestError(`Bad request "${method} ${path}" -> ${output.status}. ${base.message}`);
        self.stack = base.stack;
        if (base.cause) self.cause = base.cause;
        return self;
    }

    static async clarify(response: Response): Promise<string> {
        if (!response.bodyUsed) {
            let body: unknown;
            try {
                body = await response.json();
            } catch {
                body = null;
            }
            if (isReportableServerError(body)) {
                return `Server said "${body.message}"`;
            }
        }
        return '';
    }
}

export function isAbortError(error: unknown): error is DOMException & {name: 'AbortError'} {
    return error instanceof DOMException && error.name == 'AbortError';
}

export class ServerError extends Error {
    readonly name = 'ServerError';
    static from(output: HttpClient.SyncOutput, base: Error) {
        const {method, path} = representRequest(output.request);
        const self = new ServerError(
            `Server error on "${method} ${path}" -> ${output.status} ${output.statusText}. ${base.message}`
        );
        self.stack = base.stack;
        if (base.cause) self.cause = base.cause;
        return self;
    }
}

export class UnexpectedResponseError extends Error {
    readonly name = 'UnexpectedResponseError';
    static from(output: HttpClient.SyncOutput, base: Error) {
        const {method, path} = representRequest(output.request);
        const self = new UnexpectedResponseError(
            `Unexpected response to "${method} ${path}" -> ${output.status}. ${base.message}`
        );
        self.stack = base.stack;
        if (base.cause) self.cause = base.cause;
        return self;
    }
}

function representRequest(request: Request) {
    const method = request.method.toUpperCase();
    const path = request.url.replace(new URL(request.url).origin, '');
    return {method, path};
}
